/* ct-motion.js : Cerebral Torque Excellence motion. One reveal system for the whole site.
   - Only elements BELOW the fold at run time are ever hidden, and only by this script:
     no JS / failed JS / reduced motion / theme editor / print = everything visible.
   - Adds .is-visible when an element enters the viewport; staggers siblings (3 steps x 70ms = 210ms max).
   - Fail-safe: anything the reader scrolls past (anchor jump, End key) is shown at once, and
     everything is shown if motion gets switched off mid-visit or the page is printed.
   - Pauses ambient CSS loops (marquees, the few kept bobs, hero card bob, book float) while their block is off-screen.
   Loaded with defer from layout/theme.liquid; gated by html.ctds (theme setting ctds_enabled). */
(function () {
  'use strict';
  var d = document, root = d.documentElement;
  if (window.__ctdsMotion || !root.classList.contains('ctds')) return;
  window.__ctdsMotion = 1;
  window.__ctReveal = 1; // older inline reveal copies check this flag (also set in <head>, hook H3)

  /* Cards and bands that get a reveal without markup changes. Keep it short.
     Do not add whole bands that contain cards already listed: nested reveals double the motion. */
  var AUTO = [
    '.ctx__card', '.ct-why__card', '.section-featured-blog__wrapper > article',
    '.template-blog article.span-4', '.product-loop__item', '.ct-values__item', '.ct-keep__tile',
    '.ctig__card', '.tr2__card', '.ct-author', '.ytc', '.mrf', '.ubl',
    '.cxb .sec', '.cxs article.card'
  ];
  /* never touch: header, fixed widgets, popups, product hero, anything the user sees first,
     or blocks that already run their own reveal (.cte-rv, .reveal) */
  var SKIP = '.shopify-section-group-header-group, #shopify-section-mailing-popup, #shopify-section-night-mode, ' +
             '.ct-buddy, .ct-progress, .ct-pbar, .product__section--main, .cph, .ct-hero, .slideout, .js-modal, ' +
             '.cte-rv, .reveal, [data-ct-reveal-skip]';
  var LOOPS = '.cph, .ct-hero, .ctbk, .ct-marquee, .ct-pbar, .ubl, .mrf, .ytc, .tr2, .ct-search-empty, .sponsors-container';

  var mq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  function reduced() { return !!(mq && mq.matches); }
  function editor() { return !!(window.Shopify && window.Shopify.designMode); }
  var canIO = 'IntersectionObserver' in window;
  var pending = [];

  function settle(el) { // after the entrance, hand the element back to its own hover transitions
    setTimeout(function () { el.classList.remove('ctds-rv'); el.style.removeProperty('--ct-i'); }, 600 + 210 + 120);
  }
  function show(el) {
    if (!el.classList.contains('ctds-rv') || el.classList.contains('is-visible')) return;
    el.classList.add('is-visible');
    if (io) io.unobserve(el);
    var k = pending.indexOf(el); if (k > -1) pending.splice(k, 1);
    settle(el);
  }
  var io = canIO ? new IntersectionObserver(function (entries) {
    entries.forEach(function (e) { if (e.isIntersecting || e.boundingClientRect.top < 0) show(e.target); });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }) : null;

  function tag(scope) {
    AUTO.forEach(function (sel) {
      var list; try { list = scope.querySelectorAll(sel); } catch (err) { return; }
      list.forEach(function (el) { if (!el.hasAttribute('data-ct-reveal')) el.setAttribute('data-ct-reveal', ''); });
    });
  }

  function init(scope) {
    scope = scope || d;
    if (reduced() || editor() || !io) return; // leave everything visible
    tag(scope);
    var vh = window.innerHeight || root.clientHeight || 800;
    scope.querySelectorAll('[data-ct-reveal]').forEach(function (el) {
      if (el.__ctds) return; el.__ctds = 1;
      if (el.closest(SKIP) || el.querySelector('.cte-rv, .reveal')) return;
      if (el.parentElement && el.parentElement.closest('.ctds-rv')) return; // no nested reveals
      var cs = getComputedStyle(el);
      if (cs.position === 'fixed' || cs.display === 'none') return;
      var r = el.getBoundingClientRect();
      if (r.height === 0 || r.top < vh * 0.92) return; // already on screen, above it, or hidden: never animate
      var parent = el.parentElement, i = 0;
      if (parent) { // stagger among revealing siblings, capped at 3 steps (210ms)
        var sibs = [].filter.call(parent.children, function (c) { return c.hasAttribute('data-ct-reveal'); });
        i = Math.max(0, Math.min(sibs.indexOf(el), 3));
        if (sibs.length > 1 || parent.closest('[data-ct-reveal-group]')) el.style.setProperty('--ct-i', i);
      }
      el.classList.add('ctds-rv');
      pending.push(el);
      io.observe(el);
    });
    watchScroll();
  }

  /* fail-safe for jumps: anything whose top is above the viewport bottom is shown */
  var ticking = false, watching = false;
  function sweep() {
    ticking = false;
    var vh = window.innerHeight || 800;
    pending.slice().forEach(function (el) { if (el.getBoundingClientRect().top < vh) show(el); });
    if (!pending.length && watching) { d.removeEventListener('scroll', onScroll, true); watching = false; }
  }
  function onScroll() { if (!ticking) { ticking = true; (window.requestAnimationFrame || setTimeout)(sweep); } }
  function watchScroll() {
    /* capture on document: on phones the theme scrolls <body>, not the window, and scroll events don't bubble */
    if (pending.length && !watching) { d.addEventListener('scroll', onScroll, { capture: true, passive: true }); watching = true; }
  }

  function loops() {
    if (!canIO) return;
    var lo = new IntersectionObserver(function (es) {
      es.forEach(function (e) { e.target.classList.toggle('ctds-paused', !e.isIntersecting); });
    }, { rootMargin: '120px 0px' });
    d.querySelectorAll(LOOPS).forEach(function (el) { lo.observe(el); });
  }

  function showAll() { pending.slice().forEach(show); d.querySelectorAll('.ctds-rv').forEach(function (el) { el.classList.add('is-visible'); }); }

  function boot() {
    try { init(d); loops(); } catch (err) { showAll(); }
  }
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', boot); else boot();
  window.addEventListener('load', function () { try { init(d); sweep(); } catch (err) { showAll(); } }); // late content (app widgets, injected cards)
  window.addEventListener('beforeprint', showAll);
  window.addEventListener('pageshow', function (e) { if (e.persisted) sweep(); });
  if (mq) { var onMq = function () { if (reduced()) showAll(); }; if (mq.addEventListener) mq.addEventListener('change', onMq); else if (mq.addListener) mq.addListener(onMq); }
  d.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
